import React, { useState, useEffect, useMemo, useContext } from "react";
import { 
  Box, 
  Typography, 
  Paper, 
  Tabs, 
  Tab, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Select, 
  MenuItem, 
  TableSortLabel, 
  FormControl, 
  InputLabel,
  Button,
  CircularProgress,
  Autocomplete,
  TextField,
  Chip,
  Popper
} from "@mui/material";
import { Description as DescriptionIcon, GetApp as GetAppIcon, Settings as SettingsIcon } from '@mui/icons-material';
import { AppContext } from "../../store/AppContext";
import ExportModal from './ExportModal';
import Papa from 'papaparse';

// Define all report fields
const reportFields = [
  'centerName',
  'centerState',
  'interviewsProvisioned',
  'interviewsCreatedLTD',
  'interviewsCreatedLifetime',
  'interviewsDeletedLTD',
  'interviewsDeletedLifetime',
  'interviewsExpectedCreatedLYE',
  'interviewsExpectedUtilizationLYE',
  'interviewsActiveLTD',
  'interviewsArchivedLTD',
  'interviewsActiveLifetime',
  'interviewsArchivedLifetime',
  'interviewsSharedLTD',
  'interviewsSharedLifetime',
  'healthRecordsProvisioned',
  'healthRecordsCreatedLTD',
  'healthRecordsCreatedLifetime',
  'healthRecordsDeletedLTD',
  'healthRecordsDeletedLifetime',
  'healthRecordsExpectedCreatedLYE',
  'healthRecordsExpectedUtilizationLYE',
  'healthRecordsActiveLTD',
  'healthRecordsArchivedLTD',
  'healthRecordsActiveLifetime',
  'healthRecordsArchivedLifetime',
  'healthRecordsSharedLTD',
  'healthRecordsSharedLifetime',
  'centerFilesStorageProvisioned',
  'centerFilesStorageUsedLifetime',
  'streamingVideoPlaybackCountLTD',
  'encryptedDownloadsLTD',
  'encryptedDownloadsLifetime',
  'originalDownloadsLTD',
  'originalDownloadsLifetime',
  'numberOfUsers',
  'liveLicensesProvisioned',
  'liveRoomLicensesProvisioned',
  'liveSessionsCreatedLTD',
  'liveSessionsCreatedLifetime',
  'liveSessionsCompletedLTD',
  'liveSessionsCompletedLifetime',
  'liveRecordingsLTD',
  'liveRecordingsLifetime',
];


// Define a function to calculate column width based on key length
const calculateColumnWidth = (key, baseWidth = 100, ratio = 2.2) => {
  // Special case for centerName
  if (key === 'centerName') {
    return '250px';
  }
  
  // Special case for centerState
  if (key === 'centerState') {
    return '120px';
  }
  
  // Calculate width based on key length
  // The longer the key, the wider the column
  const keyLength = key.length;
  const calculatedWidth = baseWidth + (keyLength * ratio);
  
  return `${calculatedWidth}px`;
};

// Generate column widths dynamically
const columnWidths = {};
reportFields.forEach(field => {
  columnWidths[field] = calculateColumnWidth(field);
});

// Define column groups
const columnGroups = {
  common: ['centerName', 'centerState', 'numberOfUsers'],
  cases: [
    'casesProvisioned',
    'casesCreatedLTD',
    'casesCreatedLifetime',
    'casesDeletedLTD',
    'casesDeletedLifetime',
    'casesExpectedCreatedLYE',
    'casesExpectedUtilizationLYE',
    'casesActiveLTD',
    'casesArchivedLTD',
    'casesActiveLifetime',
    'casesArchivedLifetime',
    'casesSharedLTD',
    'casesSharedLifetime',
    'casesExpectedCreatedLifetime'
  ],
  records: [
    'recordsProvisioned',
    'recordsCreatedLTD',
    'recordsCreatedLifetime',
    'recordsDeletedLTD',
    'recordsDeletedLifetime',
    'recordsExpectedCreatedLYE',
    'recordsExpectedUtilizationLYE',
    'recordsActiveLTD',
    'recordsArchivedLTD',
    'recordsActiveLifetime',
    'recordsArchivedLifetime',
    'recordsSharedLTD',
    'recordsSharedLifetime',
    'recordsExpectedCreatedLifetime'
  ],
  interviews: [
    'interviewsProvisioned',
    'interviewsCreatedLTD',
    'interviewsCreatedLifetime',
    'interviewsDeletedLTD',
    'interviewsDeletedLifetime',
    'interviewsExpectedCreatedLYE',
    'interviewsExpectedUtilizationLYE',
    'interviewsActiveLTD',
    'interviewsArchivedLTD',
    'interviewsActiveLifetime',
    'interviewsArchivedLifetime',
    'interviewsSharedLTD',
    'interviewsSharedLifetime',
    'interviewsExpectedCreatedLifetime'
  ],
  healthRecords: [
    'healthRecordsProvisioned',
    'healthRecordsCreatedLTD',
    'healthRecordsCreatedLifetime',
    'healthRecordsDeletedLTD',
    'healthRecordsDeletedLifetime',
    'healthRecordsExpectedCreatedLYE',
    'healthRecordsExpectedUtilizationLYE',
    'healthRecordsActiveLTD',
    'healthRecordsArchivedLTD',
    'healthRecordsActiveLifetime',
    'healthRecordsArchivedLifetime',
    'healthRecordsSharedLTD',
    'healthRecordsSharedLifetime',
    'healthRecordsExpectedCreatedLifetime'
  ],
  files: [
    'centerFilesStorageProvisioned',
    'centerFilesStorageUsedLifetime',
    'streamingVideoPlaybackCountLTD',
    'encryptedDownloadsLTD',
    'encryptedDownloadsLifetime',
    'originalDownloadsLTD',
    'originalDownloadsLifetime',
    'streamingVideoPlaybackCountLifetime',
    'streamableVideosCreatedLTD',
    'streamableVideosCreatedLifetime'
  ],
  live: [
    'liveLicensesProvisioned',
    'liveRoomLicensesProvisioned',
    'liveSessionsCreatedLTD',
    'liveSessionsCreatedLifetime',
    'liveSessionsCompletedLTD',
    'liveSessionsCompletedLifetime',
    'liveRecordingsLTD',
    'liveRecordingsLifetime'
  ]
};

const columnGlossary = {
  // Common
  centerName: "The name of the account or organization.",
  centerState: "The state where the account is based.",
  numberOfUsers: "The total number of users associated with the account.",

  // Cases
  casesProvisioned: "Total number of cases provisioned to the account for the license year.",
  casesCreatedLTD: "Total number of cases created from the start of the license to date.",
  casesCreatedLifetime: "Total number of cases created over the account's entire lifetime.",
  casesDeletedLTD: "Total number of cases deleted from the start of the license to date.",
  casesDeletedLifetime: "Total number of cases deleted over the account's entire lifetime.",
  casesExpectedCreatedLYE: "Projected number of cases expected to be created by license year end.",
  casesExpectedUtilizationLYE: "Projected utilization percentage of cases by license year end.",
  casesActiveLTD: "Number of active (open) cases to date.",
  casesArchivedLTD: "Number of archived (closed/inactive) cases to date.",
  casesActiveLifetime: "Total active cases over the account's lifetime.",
  casesArchivedLifetime: "Total archived cases over the account's lifetime.",
  casesSharedLTD: "Total number of cases shared from the start of the license to date.",
  casesSharedLifetime: "Total number of shared cases over the account's lifetime.",
  casesExpectedCreatedLifetime: "Estimated total number of cases expected to be created over the lifetime of the account.",

  // Records
  recordsProvisioned: "Total number of records provisioned to the account for the license year.",
  recordsCreatedLTD: "Total number of records created from the start of the license to date.",
  recordsCreatedLifetime: "Total number of records created over the account's lifetime.",
  recordsDeletedLTD: "Total number of records deleted from the start of the license to date.",
  recordsDeletedLifetime: "Total number of records deleted over the account's lifetime.",
  recordsExpectedCreatedLYE: "Projected number of records to be created by license year end.",
  recordsExpectedUtilizationLYE: "Projected utilization percentage of records by license year end.",
  recordsActiveLTD: "Number of active records to date.",
  recordsArchivedLTD: "Number of archived records to date.",
  recordsActiveLifetime: "Total active records over the account's lifetime.",
  recordsArchivedLifetime: "Total archived records over the account's lifetime.",
  recordsSharedLTD: "Total number of records shared from the start of the license to date.",
  recordsSharedLifetime: "Total number of records shared over the account's lifetime.",
  recordsExpectedCreatedLifetime: "Projected total number of records expected to be created over the account's lifetime.",

  // Interviews
  interviewsProvisioned: "Total number of interviews provisioned to the account for the license year.",
  interviewsCreatedLTD: "Total number of interviews created from the start of the license to date.",
  interviewsCreatedLifetime: "Total number of interviews created over the account's lifetime.",
  interviewsDeletedLTD: "Total number of interviews deleted from the start of the license to date.",
  interviewsDeletedLifetime: "Total number of interviews deleted over the account's lifetime.",
  interviewsExpectedCreatedLYE: "Projected number of interviews to be created by license year end.",
  interviewsExpectedUtilizationLYE: "Projected utilization percentage of interviews by license year end.",
  interviewsActiveLTD: "Number of active interviews to date.",
  interviewsArchivedLTD: "Number of archived interviews to date.",
  interviewsActiveLifetime: "Total number of interviews active over the account's lifetime.",
  interviewsArchivedLifetime: "Total number of archived interviews over the account's lifetime.",
  interviewsSharedLTD: "Total number of interviews shared from the start of the license to date.",
  interviewsSharedLifetime: "Total number of interviews shared over the account's lifetime.",
  interviewsExpectedCreatedLifetime: "Projected total number of interviews expected to be created over the account's lifetime.",

  // Health Records
  healthRecordsProvisioned: "Total number of health records provisioned to the account for the license year.",
  healthRecordsCreatedLTD: "Total number of health records created from the start of the license to date.",
  healthRecordsCreatedLifetime: "Total number of health records created over the account's lifetime.",
  healthRecordsDeletedLTD: "Total number of health records deleted from the start of the license to date.",
  healthRecordsDeletedLifetime: "Total number of health records deleted over the account's lifetime.",
  healthRecordsExpectedCreatedLYE: "Projected number of health records to be created by license year end.",
  healthRecordsExpectedUtilizationLYE: "Projected utilization percentage of health records by license year end.",
  healthRecordsActiveLTD: "Number of active health records to date.",
  healthRecordsArchivedLTD: "Number of archived health records to date.",
  healthRecordsActiveLifetime: "Total number of health records active over the account's lifetime.",
  healthRecordsArchivedLifetime: "Total number of archived health records over the account's lifetime.",
  healthRecordsSharedLTD: "Total number of health records shared from the start of the license to date.",
  healthRecordsSharedLifetime: "Total number of health records shared over the account's lifetime.",
  healthRecordsExpectedCreatedLifetime: "Projected total number of health records expected to be created over the account's lifetime.",

  // Files
  centerFilesStorageProvisioned: "Amount of file storage (in MB/GB) provisioned for the account.",
  centerFilesStorageUsedLifetime: "Total file storage used over the account's lifetime.",
  streamingVideoPlaybackCountLTD: "Number of times streamable videos have been played from the start of the license to date.",
  encryptedDownloadsLTD: "Number of encrypted files downloaded from the start of the license to date.",
  encryptedDownloadsLifetime: "Total number of encrypted files downloaded over the account's lifetime.",
  originalDownloadsLTD: "Number of original (non-encrypted) files downloaded from the start of the license to date.",
  originalDownloadsLifetime: "Total number of original files downloaded over the account's lifetime.",
  streamingVideoPlaybackCountLifetime: "Total number of streamable video playbacks over the account's lifetime.",
  streamableVideosCreatedLTD: "Total number of streamable videos created from the start of the license to date.",
  streamableVideosCreatedLifetime: "Total number of streamable videos created over the account's lifetime.",

  // Live
  liveLicensesProvisioned: "Number of live sessions provisioned to the account for the license year.",
  liveRoomLicensesProvisioned: "Number of live virtual room licenses provisioned to the account.",
  liveSessionsCreatedLTD: "Total number of live sessions created from the start of the license to date.",
  liveSessionsCreatedLifetime: "Total number of live sessions created over the account's lifetime.",
  liveSessionsCompletedLTD: "Total number of completed live sessions from the start of the license to date.",
  liveSessionsCompletedLifetime: "Total number of completed live sessions over the account's lifetime.",
  liveRecordingsLTD: "Total number of live session recordings created from the start of the license to date.",
  liveRecordingsLifetime: "Total number of live session recordings over the account's lifetime."
};



const UsageTable = ({ 
  onOpenGlossary, 
  onOpenSettings,
  usageData,
  loading = false,
  onMonthYearChange
}) => {
  const { state } = useContext(AppContext);
  const isPrimary = state.currentCohort?.role === 'primary';
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    const monthName = now.toLocaleString('default', { month: 'long' });
    return `${monthName} ${now.getFullYear()}`;
  });
  const [sortField, setSortField] = useState('centerName');
  const [sortDirection, setSortDirection] = useState('asc');
  const [tableHeight, setTableHeight] = useState(400);
  const [exportModalOpen, setExportModalOpen] = useState(false);

  // Define the 'All' option
  const allAccountOption = { accountId: 'ALL', accountName: 'All' };

  // Get accounts from current cohort, prepend 'All' option
  const cohortAccounts = useMemo(() => {
    if (!state.currentCohort?.accounts) return [allAccountOption];
    return [allAccountOption, ...state.currentCohort.accounts];
  }, [state.currentCohort, allAccountOption]);

  // Reset tab selection when cohort changes
  useEffect(() => {
    setSelectedTab(0); // Reset to ALL tab
    setSelectedAccounts([allAccountOption]); // Reset account filter
    setSortField('centerName'); // Reset sort to default
    setSortDirection('asc');
  }, [state.reportingCohortId]);

  // Add effect to monitor usageData changes
  useEffect(() => {
  }, [usageData]);

  // Determine which tabs should be visible based on data
  const visibleTabs = useMemo(() => {
    // If no data or loading, only show ALL tab
    if (!usageData?.length || loading) {
      return ['ALL'];
    }

    const tabs = ['ALL'];
    const hasLEPro = usageData.some(item => {
      return ['LE', 'Pro', 'LE_PRO'].includes(item.edition);
    });
    const hasCACExpress = usageData.some(item => {
      const edition = item.edition?.toUpperCase();
      return ['CAC', 'EXPRESS', 'CAC_EXPRESS'].includes(edition);
    });

    if (hasLEPro) {
      tabs.push('CASES', 'RECORDS');
    }
    if (hasCACExpress) {
      tabs.push('INTERVIEWS', 'HEALTH RECORDS');
    }
    tabs.push('FILES', 'LIVE SESSIONS');
    return tabs;
  }, [usageData, loading]);

  // Determine which columns should be visible based on selected tab and data
  const visibleColumns = useMemo(() => {
    const hasLEPro = usageData.some(item => {
      return ['LE', 'Pro', 'LE_PRO'].includes(item.edition);
    });
    const hasCACExpress = usageData.some(item => {
      const edition = item.edition?.toUpperCase();
      return ['CAC', 'EXPRESS', 'CAC_EXPRESS'].includes(edition);
    });

    // Common columns for all tabs except ALL
    const commonColumns = ['centerName', 'centerState'];

    // Map tab names to their corresponding column groups
    const tabToColumns = {
      'ALL': [
        ...columnGroups.common,
        ...(hasLEPro ? columnGroups.cases : []),
        ...(hasLEPro ? columnGroups.records : []),
        ...(hasCACExpress ? columnGroups.interviews : []),
        ...(hasCACExpress ? columnGroups.healthRecords : []),
        ...columnGroups.files,
        ...columnGroups.live
      ],
      'CASES': hasLEPro ? [...commonColumns, ...columnGroups.cases] : commonColumns,
      'RECORDS': hasLEPro ? [...commonColumns, ...columnGroups.records] : commonColumns,
      'INTERVIEWS': hasCACExpress ? [...commonColumns, ...columnGroups.interviews] : commonColumns,
      'HEALTH RECORDS': hasCACExpress ? [...commonColumns, ...columnGroups.healthRecords] : commonColumns,
      'FILES': [...commonColumns, ...columnGroups.files],
      'LIVE SESSIONS': [...commonColumns, ...columnGroups.live]
    };

    // Get the current tab name based on selectedTab index
    const currentTab = visibleTabs[selectedTab];

    return tabToColumns[currentTab] || commonColumns;
  }, [selectedTab, usageData, visibleTabs]);

  // Generate month options dynamically
  const generateMonthOptions = () => {
    const options = [];
    const startDate = new Date(2025, 2); // March 2025 (month is 0-based)
    const currentDate = new Date();
    const endDate = new Date(currentDate.getFullYear(), currentDate.getMonth());

    let currentDatePointer = new Date(startDate);
    while (currentDatePointer <= endDate) {
      const year = currentDatePointer.getFullYear();
      const month = currentDatePointer.getMonth();
      const monthName = currentDatePointer.toLocaleString('default', { month: 'long' });
      
      options.push({
        display: `${monthName} ${year}`,
        value: `${year}-${String(month + 1).padStart(2, '0')}`
      });

      // Move to next month
      currentDatePointer.setMonth(currentDatePointer.getMonth() + 1);
    }

    return options;
  };

  const monthOptions = generateMonthOptions();

  useEffect(() => {
    const handleResize = () => {
      const headerHeight = document.querySelector('header')?.offsetHeight || 0;
      const availableHeight = window.innerHeight - headerHeight - 200; // Adjusted offset for better fit
      setTableHeight(availableHeight);
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  // Handle month change
  const handleMonthChange = (event) => {
    const newMonth = event.target.value;
    setSelectedMonth(newMonth);
    if (onMonthYearChange) {
      // Find the API value for the selected month
      const selectedOption = monthOptions.find(option => option.display === newMonth);
      if (selectedOption) {
        onMonthYearChange(selectedOption.value);
      }
    }
  };

  // Selection logic for Autocomplete
  const handleAccountsChange = (event, newValue, reason) => {
    // Prevent 'All' from being removed if it's the only selected value
    // if selectedAccounts contains all and newValue only contains all, set selectedAccounts to all
    if (selectedAccounts.some(acc => acc.accountId === 'ALL') && newValue.every(acc => acc.accountId === 'ALL')) {
      setSelectedAccounts([allAccountOption]);
      return;
    }

    // Prevent 'All' from being deselected if it's the only selected value (fallback)
    if (selectedAccounts.length === 1 && selectedAccounts[0].accountId === 'ALL' && (!newValue || newValue.length === 0)) {
      setSelectedAccounts([allAccountOption]);
      return;
    }
    // If user clears all, select 'All'
    if (!newValue || newValue.length === 0) {
      setSelectedAccounts([allAccountOption]);
      return;
    }
    // If 'All' was just selected, set to only 'All'
    if (newValue.some(acc => acc.accountId === 'ALL') &&
        !selectedAccounts.some(acc => acc.accountId === 'ALL')) {
      setSelectedAccounts([allAccountOption]);
      return;
    }
    // If 'All' is currently selected and user selects another, remove 'All'
    if (selectedAccounts.some(acc => acc.accountId === 'ALL') &&
        newValue.some(acc => acc.accountId !== 'ALL')) {
      setSelectedAccounts(newValue.filter(acc => acc.accountId !== 'ALL'));
      return;
    }
    // Otherwise, normal multi-select
    setSelectedAccounts(newValue.filter(acc => acc.accountId !== 'ALL'));
  };

  // Filter data based on selected filters
  const filteredData = usageData.filter(row => {
    // If 'All' is selected, show all data
    if (selectedAccounts.some(acc => acc.accountId === 'ALL')) {
      return true;
    }
    // Filter by selected accounts
    return selectedAccounts.some(account => 
      account.accountName === row.centerName
    );
  });

  // Sort data
  const sortedData = [...filteredData].sort((a, b) => {
    if (sortField === 'centerName') {
      const aValue = a[sortField] || '';
      const bValue = b[sortField] || '';
      return sortDirection === 'asc' 
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }
    
    // For numeric fields
    const aValue = a[sortField] || 0;
    const bValue = b[sortField] || 0;
    
    return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
  });

  // Handle sort
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Format column headers for display
  const formatColumnHeader = (field) => {
    // Convert camelCase to Title Case with spaces
    return field
      .replace(/([A-Z])/g, ' $1') // Add space before capital letters
      .replace(/^./, str => str.toUpperCase()) // Capitalize first letter
      .replace(/L T D/g, 'LTD') // Fix LTD spacing
      .replace(/L Y E/g, 'LYE') // Fix LYE spacing
      .replace(/Provisioned/g, 'Provisioned')
      .replace(/Created/g, 'Created')
      .replace(/Deleted/g, 'Deleted')
      .replace(/Active/g, 'Active')
      .replace(/Archived/g, 'Archived')
      .replace(/Shared/g, 'Shared')
      .replace(/Used/g, 'Used')
      .replace(/Count/g, 'Count')
      .replace(/Downloads/g, 'Downloads')
      .replace(/Users/g, 'Users')
      .replace(/Licenses/g, 'Licenses')
      .replace(/Sessions/g, 'Sessions')
      .replace(/Recordings/g, 'Recordings')
      .replace(/Storage/g, 'Storage')
      .replace(/Video/g, 'Video')
      .replace(/Playback/g, 'Playback')
      .replace(/Encrypted/g, 'Encrypted')
      .replace(/Original/g, 'Original')
      .replace(/Room/g, 'Room')
      .replace(/Center/g, 'Center')
      .replace(/State/g, 'State')
      .replace(/Name/g, 'Name');
  };

  // Add function to format storage values
  const formatStorageValue = (value) => {
    if (typeof value !== 'number' || value <= 0) return '-';
    
    const TB = 1024 * 1024 * 1024 * 1024; // 1 TB in bytes
    const GB = 1024 * 1024 * 1024; // 1 GB in bytes
    
    if (value >= TB) {
      return `${(value / TB).toFixed(2)} TB`;
    } else {
      return `${(value / GB).toFixed(2)} GB`;
    }
  };

  // Add function to format general numeric values
  const formatValue = (value) => {
    if (typeof value !== 'number' || value <= 0) return '-';
    return value.toLocaleString();
  };

  // Add a function to calculate the minimum table width based on visible columns
  const calculateMinTableWidth = () => {
    // Sum up the widths of all visible columns
    const totalWidth = visibleColumns.reduce((sum, column) => {
      // Extract the numeric value from the width string (e.g., '250px' -> 250)
      const widthValue = parseInt(columnWidths[column] || '100px', 10);
      return sum + widthValue;
    }, 0);
    
    // Add some padding
    return `${totalWidth + 50}px`;
  };

  // Add function to check if a column is a storage column
  const isStorageColumn = (column) => {
    return column.toLowerCase().includes('storage');
  };

  // Add function to check if a column should be formatted as a number
  const isNumericColumn = (column) => {
    return !['centerName', 'centerState'].includes(column);
  };

  // Add function to calculate percentage
  const calculatePercentage = (rows, numeratorField, denominatorField) => {
    const numerator = rows.reduce((acc, row) => acc + (row[numeratorField] || 0), 0);
    const denominator = rows.reduce((acc, row) => acc + (row[denominatorField] || 0), 0);
    if (!denominator) return '-';
    const percentage = (numerator / denominator) * 100;
    return percentage.toFixed(2) + '%';
  };

  // Add function to check if a column is a utilization column
  const isUtilizationColumn = (column) => {
    return column.toLowerCase().includes('utilization');
  };

  // Add function to get utilization calculation fields
  const getUtilizationFields = (column) => {
    switch (column) {
      case 'interviewsExpectedUtilizationLYE':
        return {
          numerator: 'interviewsCreatedLTD',
          denominator: 'interviewsProvisioned'
        };
      case 'healthRecordsExpectedUtilizationLYE':
        return {
          numerator: 'healthRecordsCreatedLTD',
          denominator: 'healthRecordsProvisioned'
        };
      case 'casesExpectedUtilizationLYE':
        return {
          numerator: 'casesCreatedLTD',
          denominator: 'casesProvisioned'
        };
      case 'recordsExpectedUtilizationLYE':
        return {
          numerator: 'recordsCreatedLTD',
          denominator: 'recordsProvisioned'
        };
      default:
        return null;
    }
  };

  // Add function to calculate percentage for a single row
  const calculateRowPercentage = (row, numeratorField, denominatorField) => {
    const numerator = row[numeratorField] || 0;
    const denominator = row[denominatorField] || 0;
    if (!denominator) return '-';
    const percentage = (numerator / denominator) * 100;
    return percentage.toFixed(2) + '%';
  };

  const handleExportClick = () => {
    setExportModalOpen(true);
  };

  const handleExportClose = () => {
    setExportModalOpen(false);
  };

  const handleExport = (exportConfig) => {
    const { exportOption, account, month, tab } = exportConfig;

    // 1. Filter by account
    let filtered = usageData;
    if (
      account &&
      account.length > 0 &&
      !account.some(acc => acc.accountId === 'ALL')
    ) {
      const selectedNames = account.map(acc => acc.accountName);
      filtered = filtered.filter(row => selectedNames.includes(row.centerName));
    }

    // 2. Filter by month if needed (add logic if your data is month-specific)

    // 3. Use visibleColumns for export
    const columns = visibleColumns;

    // 4. Prepare data for CSV
    const dataToExport = filtered.map(row =>
      columns.reduce((acc, column) => {
        if (isUtilizationColumn(column)) {
          const fields = getUtilizationFields(column);
          const value = fields ? calculateRowPercentage(row, fields.numerator, fields.denominator) : '';
          acc[column] = value === '-' ? '' : value;
        } else if (isStorageColumn(column)) {
          const value = formatStorageValue(row[column]);
          acc[column] = value === '-' ? '' : value;
        } else if (isNumericColumn(column)) {
          const value = formatValue(row[column]);
          acc[column] = value === '-' ? '' : value;
        } else {
          acc[column] = row[column] || '';
        }
        return acc;
      }, {})
    );

    // 5. Prepare header row (formatted labels)
    const headerRow = columns.map(col => formatColumnHeader(col));

    // 6. Generate CSV with custom headers
    const safeMonth = (month || 'all-months').replace(/\s+/g, '-').toLowerCase();
    const filename = `guardify-cohort-${safeMonth}.csv`;
    const csv = Papa.unparse({
      data: [headerRow, ...dataToExport.map(row => columns.map(col => row[col]))]
    });

    // 7. Download
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Add function to build glossary items
  const buildGlossaryItems = () => {
    return visibleColumns.map(column => ({
      title: formatColumnHeader(column),
      description: columnGlossary[column] || 'No description available'
    }));
  };

  const handleOpenGlossary = () => {
    const glossaryItems = buildGlossaryItems();
    onOpenGlossary(glossaryItems);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        mb: 2
      }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Autocomplete
            multiple
            options={cohortAccounts}
            getOptionLabel={(option) => option.accountName}
            value={Array.isArray(selectedAccounts) && selectedAccounts.length > 0 ? selectedAccounts : [allAccountOption]}
            onChange={handleAccountsChange}
            clearIcon={null}
            PopperComponent={(props) => (
              <Popper
                {...props}
                style={{ width: 'max-content', minWidth: '200px' }}
                placement="bottom-start"
              />
            )}
            renderTags={(value, getTagProps) => {
              const isAll = value.length === 1 && value[0].accountId === 'ALL';
              if (isAll) {
                return [
                  <span key="all-text" style={{ color: '#444', fontSize: 15, marginLeft: 4 }}>
                    All
                  </span>
                ];
              }
              return value.map((option, index) => {
                const { key, ...chipProps } = getTagProps({ index });
                return (
                  <Chip
                    key={key}
                    label={option.accountName}
                    sx={{ 
                      background: '#e0e0e0', 
                      fontSize: 13,
                      maxWidth: 200,
                      '& .MuiChip-label': {
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }
                    }}
                    {...chipProps}
                  />
                );
              });
            }}
            renderOption={(props, option) => (
              <li {...props}>
                <Typography
                  sx={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    width: '100%'
                  }}
                >
                  {option.accountName}
                </Typography>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="standard"
                label="Accounts"
                sx={{ 
                  minWidth: 200,
                  '& .MuiAutocomplete-input': {
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }
                }}
              />
            )}
            sx={{ 
              minWidth: 200,
              '& .MuiAutocomplete-tag': {
                maxWidth: '200px'
              }
            }}
          />
          <FormControl variant="standard" sx={{ minWidth: 120 }}>
            <InputLabel>Month:</InputLabel>
            <Select 
              value={selectedMonth} 
              onChange={handleMonthChange} 
              sx={{ borderBottom: '1px solid #000' }} 
              disableUnderline
            >
              {monthOptions.map((option) => (
                <MenuItem key={option.value} value={option.display}>
                  {option.display}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={handleOpenGlossary} sx={{ backgroundColor: '#f0f0f0', color: 'primary.main', minWidth: 70, height: 70, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', '&:hover': { backgroundColor: '#e0e0e0' } }}>
            <DescriptionIcon sx={{ fontSize: 30 }} />
            <Typography variant="caption">Glossary</Typography>
          </Button>
          <Button 
            onClick={handleExportClick}
            sx={{ backgroundColor: '#f0f0f0', color: 'primary.main', minWidth: 70, height: 70, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', '&:hover': { backgroundColor: '#e0e0e0' } }}
          >
            <GetAppIcon sx={{ fontSize: 30 }} />
            <Typography variant="caption">Export</Typography>
          </Button>
          {isPrimary && (
            <Button onClick={onOpenSettings} sx={{ backgroundColor: '#f0f0f0', color: 'primary.main', minWidth: 70, height: 70, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', '&:hover': { backgroundColor: '#e0e0e0' } }}>
              <SettingsIcon sx={{ fontSize: 30 }} />
              <Typography variant="caption">Settings</Typography>
            </Button>
          )}
        </Box>
      </Box>

      <Tabs value={selectedTab} onChange={handleTabChange} aria-label="usage tabs" sx={{ backgroundColor: '#ffffff', mb: 0 }}>
        {visibleTabs.map((tab, index) => (
          <Tab key={tab} label={tab} />
        ))}
      </Tabs>

      {loading ? (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          height: '400px',
          backgroundColor: 'white',
          gap: 2,
          color: 'text.secondary'
        }}>
          <CircularProgress />
          <Typography variant="body2">Loading...</Typography>
        </Box>
      ) : sortedData.length === 0 ? (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          height: '400px',
          gap: 1,
          backgroundColor: 'white',
          color: 'text.secondary'
        }}>
          <Typography variant="h6">No Data Available</Typography>
          <Typography variant="body2">
            {selectedAccounts.length === 0 
              ? `No data found for all accounts in ${selectedMonth}`
              : selectedAccounts.length === 1
                ? `No data found for ${selectedAccounts[0].accountName} in ${selectedMonth}`
                : `No data found for selected accounts in ${selectedMonth}`}
          </Typography>
        </Box>
      ) : (
        <TableContainer 
          component={Paper} 
          sx={{ 
            mt: 0, 
            maxHeight: tableHeight,
            overflowX: 'auto',
            '& .MuiTable-root': {
              minWidth: calculateMinTableWidth(),
              tableLayout: 'fixed',
            }
          }}
        >
          <Table stickyHeader>
            <TableHead sx={{
              backgroundColor: '#f7f7f7',
              '& th.MuiTableCell-stickyHeader': {
                backgroundColor: '#f7f7f7',
              },
              '& th': { color: '#888888' }
            }}>
              <TableRow>
                {visibleColumns.map((column) => (
                  <TableCell 
                    key={column} 
                    align="left"
                    sx={{ 
                      width: columnWidths[column] || 'auto',
                      minWidth: columnWidths[column] || 'auto',
                      maxWidth: columnWidths[column] || 'auto',
                      whiteSpace: 'normal',
                      wordWrap: 'break-word',
                      padding: '8px 4px',
                      paddingLeft: '16px',
                      fontSize: '0.8rem',
                      lineHeight: 1.2,
                      verticalAlign: 'bottom'
                    }}
                  >
                    <TableSortLabel
                      active={sortField === column}
                      direction={sortField === column ? sortDirection : 'asc'}
                      onClick={() => handleSort(column)}
                    >
                      {formatColumnHeader(column)}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedData.map((row) => (
                <TableRow
                  key={row.id}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  {visibleColumns.map((column) => (
                    <TableCell 
                      key={column} 
                      component={column === 'centerName' ? 'th' : 'td'} 
                      scope={column === 'centerName' ? 'row' : undefined}
                      align="left"
                      sx={{ 
                        width: columnWidths[column] || 'auto',
                        minWidth: columnWidths[column] || 'auto',
                        maxWidth: columnWidths[column] || 'auto',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      {isUtilizationColumn(column) ? 
                        (() => {
                          const fields = getUtilizationFields(column);
                          return fields ? calculateRowPercentage(row, fields.numerator, fields.denominator) : '-';
                        })() :
                        isStorageColumn(column) 
                          ? formatStorageValue(row[column])
                          : isNumericColumn(column)
                            ? formatValue(row[column])
                            : row[column]}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
              {/* Total Row */}
              <TableRow sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                <TableCell align="left">Total</TableCell>
                {visibleColumns.slice(1).map((column) => (
                  <TableCell 
                    key={column} 
                    align="left"
                    sx={{ 
                      width: columnWidths[column] || 'auto',
                      minWidth: columnWidths[column] || 'auto',
                      maxWidth: columnWidths[column] || 'auto',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}
                  >
                    {column === 'centerState' ? '' : 
                      isUtilizationColumn(column) ? 
                        (() => {
                          const fields = getUtilizationFields(column);
                          return fields ? calculatePercentage(sortedData, fields.numerator, fields.denominator) : '-';
                        })() :
                      isStorageColumn(column)
                        ? formatStorageValue(sortedData.reduce((sum, row) => {
                            const value = row[column];
                            return sum + (typeof value === 'number' ? value : 0);
                          }, 0))
                        : isNumericColumn(column)
                          ? formatValue(sortedData.reduce((sum, row) => {
                              const value = row[column];
                              return sum + (typeof value === 'number' ? value : 0);
                            }, 0))
                          : sortedData.reduce((sum, row) => {
                              const value = row[column];
                              return sum + (typeof value === 'number' ? value : 0);
                            }, 0)}
                  </TableCell>
                ))}
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <ExportModal 
        open={exportModalOpen}
        onClose={handleExportClose}
        onExport={handleExport}
        selectedAccount={selectedAccounts}
        onAccountChange={setSelectedAccounts}
        selectedMonth={selectedMonth}
        onMonthChange={setSelectedMonth}
        monthOptions={monthOptions}
        visibleTabs={visibleTabs}
        selectedTab={selectedTab}
        onMonthYearChange={onMonthYearChange}
        cohortAccounts={cohortAccounts.filter(acc => acc.accountId !== 'ALL')}
      />
    </Box>
  );
};

export default UsageTable; 